import { create } from 'zustand';

// App appearance. Midnight is the authored look; Day and Night re-map the palette (themes.css).
export type ThemeMode = 'day' | 'night' | 'midnight';

export const THEME_MODES: ThemeMode[] = ['day', 'night', 'midnight'];

// Also read by the pre-paint script in index.html; keep the key and values in sync.
export const THEME_STORAGE_KEY = 'ezart.theme';

const DEFAULT_THEME: ThemeMode = 'midnight';

const isThemeMode = (value: unknown): value is ThemeMode =>
    typeof value === 'string' && (THEME_MODES as string[]).includes(value);

const applyTheme = (theme: ThemeMode) => {
    document.documentElement.dataset.theme = theme;
};

// index.html applies the saved theme before first paint; start from what it chose.
const initialTheme = (): ThemeMode => {
    const fromDocument = document.documentElement.dataset.theme;
    return isThemeMode(fromDocument) ? fromDocument : DEFAULT_THEME;
};

interface ThemeStore {
    theme: ThemeMode;
    setTheme: (theme: ThemeMode) => void;
}

export const useThemeStore = create<ThemeStore>((set) => ({
    theme: initialTheme(),

    setTheme: (theme: ThemeMode) => {
        applyTheme(theme);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // Storage can be unavailable (private mode); the choice still applies to this tab.
        }
        set({ theme });
    }
}));

// Keep other open tabs in step when the theme changes elsewhere.
window.addEventListener('storage', (event) => {
    if (event.key !== THEME_STORAGE_KEY || !isThemeMode(event.newValue)) return;
    applyTheme(event.newValue);
    useThemeStore.setState({ theme: event.newValue });
});
